import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  EventAvailableRounded,
  LockRounded,
  CheckCircleRounded,
  GroupRounded,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEnrolledSections } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { enrollmentOpenOf, enrollPricingOf } from '../home/portal-home.model';
import { courseColor } from './CourseShell';
import { useTenantStore } from '../../../stores/tenant.store';
import { enrollmentApi, type PortalSectionDto } from '../../../lib/api/enrollment.api';
import { computeCoursesFee, type ChargeBasis, type Currency } from '@dosfilos/domain';
import { errorText } from '../../../lib/error-text';
import { formatMoney } from '../../../lib/i18n-format';
import { sectionCapacity, sectionHasSeat } from '@dosfilos/domain';

type PaymentPlanOption = 'full' | '3-months' | '6-months' | '9-months';
const PLAN_OPTIONS: PaymentPlanOption[] = ['full', '3-months', '6-months', '9-months'];

export function EnrollPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentTenantId } = useTenantStore();
  const sectionsQ = useEnrolledSections();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [plan, setPlan] = useState<PaymentPlanOption>('full');
  const [error, setError] = useState<string | null>(null);

  const data = sectionsQ.data;
  const activeTerm = data?.activeTerm ?? null;
  const available = useMemo(() => data?.availableSections ?? [], [data]);

  // Sin aranceles configurados no se muestra precio ni moneda (ni «0» ni CLP).
  const pricing = enrollPricingOf(data);
  const fmtMoney = (n: number) => (pricing ? formatMoney(n, pricing.currency) : '');

  const selectedSections = useMemo(
    () => available.filter((s) => selected.has(s.id)),
    [available, selected],
  );
  // per_year flat fee already billed this year → enrolling adds no new charge.
  const programFeeCovered = data?.chargeBasis === 'per_year' && data?.programFeeBilled === true;

  const total = useMemo(() => {
    if (!data || !pricing) return 0;
    if (programFeeCovered) return 0; // annual program fee already paid
    const totalCredits = selectedSections.reduce((a, s) => a + (s.credits ?? 0), 0);
    // Same pure helper the enroll callable uses → shown total == billed total.
    const coursesFee = computeCoursesFee(
      {
        enrollmentFee: data.enrollmentFee,
        programTotalFee: data.programTotalFee,
        pricePerCredit: data.pricePerCredit,
        ...(data.ratePerCourse !== undefined ? { ratePerCourse: data.ratePerCourse } : {}),
        ...(data.chargeBasis ? { chargeBasis: data.chargeBasis } : {}),
        pricingMode: data.pricingMode,
        currency: pricing.currency as Currency,
      },
      { courseCount: selectedSections.length, totalCredits },
    );
    return coursesFee + data.enrollmentFee;
  }, [data, pricing, selectedSections, programFeeCovered]);

  // Installments hidden for per_course (full only) and when the program fee is
  // already covered (nothing left to schedule).
  const installmentsAllowed = data?.chargeBasis !== 'per_course' && !programFeeCovered;
  const effectivePlan: PaymentPlanOption = installmentsAllowed ? plan : 'full';

  const enrollMutation = useMutation({
    mutationFn: () =>
      enrollmentApi.enrollStudentInSections({
        tenantId: currentTenantId!,
        termId: activeTerm!.id,
        sectionIds: [...selected],
        paymentPlanOption: effectivePlan,
        // So the pay_to_enroll checkout returns to this same domain.
        returnOrigin: window.location.origin,
      }),
    onSuccess: async (result) => {
      // pay_to_enroll: enrollment is not confirmed yet — send the student to the
      // hosted checkout. The webhook confirms the enrollment on payment approval.
      if (result?.requiresPayment && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['portal-v2-sections'] });
      navigate('/portal-v2/courses');
    },
    onError: (e: unknown) => {
      setError(errorText(e, t('portal.enroll.errorGeneric')));
    },
  });

  const toggle = (id: string) => {
    setError(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (sectionsQ.isLoading) return <PortalLoading />;

  // No open enrollment period (no active term or the window is closed).
  // La misma condición que el aviso del inicio («qué sigue»).
  if (!activeTerm || !enrollmentOpenOf(data)) {
    const closesAt = activeTerm?.enrollmentWindow?.closesAt;
    return (
      <PortalEmpty
        icon={<EventAvailableRounded fontSize="inherit" />}
        title={t('portal.enroll.closedTitle')}
        body={
          closesAt
            ? t('portal.enroll.closedSince', { date: closesAt })
            : t('portal.enroll.closedBody')
        }
      />
    );
  }

  const closesAt = activeTerm.enrollmentWindow?.closesAt;

  return (
    <Box sx={{ pb: 14 }}>
      <Stack spacing={0.5} mb={3}>
        <Typography variant="h4" fontWeight={700} color="#e6edf3">
          {t('portal.enroll.title')}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="#8b949e">
            {activeTerm.name}
          </Typography>
          {closesAt && (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<EventAvailableRounded />}
              label={t('portal.enroll.openUntil', { date: closesAt })}
            />
          )}
          {data?.chargeBasis === 'per_year' && (
            <Chip
              size="small"
              color={programFeeCovered ? 'default' : 'primary'}
              variant="outlined"
              label={
                programFeeCovered
                  ? t('portal.enroll.programFeeCovered')
                  : t('portal.enroll.programFeeAnnual')
              }
            />
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {available.length === 0 ? (
        <PortalEmpty
          icon={<EventAvailableRounded fontSize="inherit" />}
          title={t('portal.enroll.emptyCatalogTitle')}
          body={t('portal.enroll.emptyCatalogBody')}
        />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          }}
        >
          {available.map((s) => (
            <CourseCard
              key={s.id}
              section={s}
              selected={selected.has(s.id)}
              onToggle={() => toggle(s.id)}
              fmtMoney={pricing ? fmtMoney : null}
              chargeBasis={data?.chargeBasis}
              pricePerCredit={data?.pricePerCredit ?? 0}
              ratePerCourse={data?.ratePerCourse ?? 0}
            />
          ))}
        </Box>
      )}

      {/* Sticky summary / confirm bar */}
      {available.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: { xs: 0, md: 240 },
            right: 0,
            bgcolor: '#161b22',
            borderTop: '1px solid #30363d',
            px: { xs: 2, md: 4 },
            py: 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            zIndex: 5,
          }}
        >
          <Stack sx={{ minWidth: 140 }}>
            <Typography variant="caption" color="#8b949e">
              {t('portal.enroll.selectedCount', { count: selected.size })}
            </Typography>
            {pricing && (
              <Typography variant="h6" fontWeight={700} color="#e6edf3">
                {t('portal.enroll.total')}: {fmtMoney(total)}
              </Typography>
            )}
          </Stack>
          {!pricing ? null : installmentsAllowed ? (
            <TextField
              select
              size="small"
              label={t('portal.enroll.paymentPlan')}
              value={plan}
              onChange={(e) => setPlan(e.target.value as PaymentPlanOption)}
              sx={{ minWidth: 200 }}
            >
              {PLAN_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {t(`portal.enroll.plan.${opt}`)}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <Chip
              variant="outlined"
              sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
              label={t('portal.enroll.plan.full')}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            size="large"
            disabled={selected.size === 0 || enrollMutation.isPending}
            onClick={() => {
              setError(null);
              enrollMutation.mutate();
            }}
            sx={{ minWidth: 160 }}
          >
            {enrollMutation.isPending ? t('portal.enroll.confirming') : t('portal.enroll.confirm')}
          </Button>
        </Paper>
      )}
    </Box>
  );
}

function CourseCard({
  section,
  selected,
  onToggle,
  fmtMoney,
  chargeBasis,
  pricePerCredit,
  ratePerCourse,
}: {
  section: PortalSectionDto;
  selected: boolean;
  onToggle: () => void;
  /** Null: the program has no fees configured — the card shows no price. */
  fmtMoney: ((n: number) => string) | null;
  chargeBasis: ChargeBasis | undefined;
  pricePerCredit: number;
  ratePerCourse: number;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const color = courseColor(section.id);
  const eligible = section.eligible !== false;
  // Cupo ≤ 0 = sin límite: la regla del dominio, la misma que aplica el servidor.
  const full = !sectionHasSeat(section.currentEnrollment, section.maxEnrollment);
  const disabled = !eligible || full;
  // Per-card incremental price only makes sense for usage-based bases; the flat
  // bases (per_term / per_year) price the whole period, not the course.
  const cardPrice =
    chargeBasis === 'per_credit'
      ? section.credits * pricePerCredit
      : chargeBasis === 'per_course'
        ? ratePerCourse
        : null;

  return (
    <Card
      sx={{
        bgcolor: '#161b22',
        border: '1px solid',
        borderColor: selected ? 'primary.main' : '#21262d',
        borderRadius: 2,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Box sx={{ height: 8, bgcolor: color }} />
      <Box sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="#8b949e">
              {section.courseCode}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
              {section.courseName}
            </Typography>
            <Typography variant="caption" color="#8b949e">
              {section.facultyName}
            </Typography>
          </Box>
          <Checkbox
            checked={selected}
            disabled={disabled}
            onChange={onToggle}
            sx={{ color: '#8b949e', '&.Mui-checked': { color: 'primary.main' }, mt: -1, mr: -1 }}
          />
        </Stack>

        <Stack direction="row" spacing={1} mt={1.5} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
            label={t('portal.enroll.credits', { count: section.credits })}
          />
          {sectionCapacity(section.maxEnrollment) !== null && section.maxEnrollment < 9999 && (
            <Chip
              size="small"
              variant="outlined"
              icon={<GroupRounded />}
              sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
              label={`${section.currentEnrollment}/${section.maxEnrollment}`}
            />
          )}
          {cardPrice !== null && fmtMoney && (
            <Chip
              size="small"
              variant="outlined"
              sx={{ borderColor: '#30363d', color: '#c9d1d9' }}
              label={fmtMoney(cardPrice)}
            />
          )}
        </Stack>

        {/* Status row: blocked (prereqs) or full take precedence over selectable. */}
        {!eligible ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            icon={<LockRounded />}
            sx={{ mt: 1.5, maxWidth: '100%', '& .MuiChip-label': { whiteSpace: 'normal' } }}
            label={t('portal.enroll.blocked', {
              reqs: (section.unmetPrereqs ?? []).join(', '),
            })}
          />
        ) : full ? (
          <Chip
            size="small"
            color="default"
            variant="outlined"
            sx={{ mt: 1.5 }}
            label={t('portal.enroll.full')}
          />
        ) : (
          selected && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              icon={<CheckCircleRounded />}
              sx={{ mt: 1.5 }}
              label={t('portal.enroll.selected')}
            />
          )
        )}
      </Box>
    </Card>
  );
}
